import React, { useState, useEffect } from 'react';
import SuperAdminSidebar from '@/components/sidebar/superAdminSidebar';
import { SuperAdminService } from '@/services/superadmin.service';
import { toast, Toaster } from 'react-hot-toast';
import { Calendar, Download, RefreshCw, BarChart2 } from 'lucide-react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import logoImg from '@/assets/Sthitih_Logo.png';
import { SuperAdminMobileHeader } from '@/components/navigation/SuperAdminMobileHeader';

interface ReportData {
  overview: {
    totalClients: number;
    active: number;
    trial: number;
    suspended: number;
    expired: number;
    totalRevenue: number;
    conversionRate: number;
    churnRate: number;
  };
  planSplit: { basic: number; professional: number; enterprise: number };
  recentGrowth: { month: string; clients: number; revenue: number }[];
}

const Reports: React.FC = () => {
  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'6m' | '12m' | 'all'>('6m');
  const [activeMetric, setActiveMetric] = useState<'both' | 'revenue' | 'clients'>('both');

  const fetchReportsData = async () => {
    try {
      setLoading(true);
      const res = await SuperAdminService.getReports();
      setData(res);
    } catch {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportsData();
  }, []);

  const handleExportCSV = () => {
    if (!data) return;
    const headers = ['Month', 'Clients Count', 'Revenue (INR)'];
    const rows = data.recentGrowth.map(g => [g.month, g.clients, g.revenue]);
    const csvContent = "data:text/csv;charset=utf-8," 
      + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `platform_growth_report_${timeRange}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Report CSV exported successfully');
  };

  return (
    <div className="flex min-h-screen bg-[#F7F2EA] text-[#243238]">
      <Toaster position="top-right" />
      <SuperAdminSidebar />

      <div className="flex-1 flex flex-col pl-0 pb-24 md:pl-20 md:pb-8 min-h-screen min-w-0 w-full relative">
        <SuperAdminMobileHeader title="Reports & Analytics" />

        {/* Header */}
        <header className="hidden md:flex h-16 px-8 bg-[#F7F2EA] border-b border-[#DDD0BE] shadow-xs items-center justify-between sticky top-0 z-20 w-full">
          <div className="flex items-center gap-2">
            <h1 className="text-sm sm:text-base font-extrabold tracking-tight text-[#243238] uppercase">Reports & Analytics</h1>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={fetchReportsData}
              className="p-2 text-[#8D8A83] hover:text-[#1F5A43] hover:bg-[#EFE5D6] rounded-lg transition-all"
              title="Refresh Data"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <img src={logoImg} alt="Sthitih Logo" className="h-10 w-auto object-contain rounded-md shadow-xs border border-black/5" />
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-grow p-8">
          {loading || !data ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-[#1F5A43] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-8 max-w-[1600px] mx-auto">
              
              {/* Top Control Bar */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-[#FFFDF9] p-4 rounded-xl border border-[#E4D8C8] shadow-earth">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#1F5A43]" />
                  <span className="text-sm font-semibold text-[#4A5558]">Report Scope:</span>
                  <div className="flex rounded-lg bg-[#F7F2EA] p-0.5 ml-2 border border-[#DDD0BE]">
                    {(['6m', '12m', 'all'] as const).map((r) => (
                      <button
                        key={r}
                        onClick={() => setTimeRange(r)}
                        className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${timeRange === r ? 'bg-[#1F5A43] text-white shadow-xs' : 'text-[#6F706C] hover:text-[#243238]'}`}
                      >
                        {r === '6m' ? '6 Months' : r === '12m' ? '12 Months' : 'All Time'}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-3 w-full sm:w-auto">
                  <div className="flex rounded-lg bg-[#F7F2EA] p-0.5 border border-[#DDD0BE]">
                    {(['both', 'revenue', 'clients'] as const).map((m) => (
                      <button
                        key={m}
                        onClick={() => setActiveMetric(m)}
                        className={`px-3 py-1 text-xs font-bold rounded-md transition-all capitalize ${activeMetric === m ? 'bg-[#1F5A43] text-white shadow-xs' : 'text-[#6F706C] hover:text-[#243238]'}`}
                      >
                        {m}
                      </button>
                    ))}
                  </div>
                  <button 
                    onClick={handleExportCSV}
                    className="btn-primary text-xs flex items-center gap-1.5 ml-auto sm:ml-0"
                  >
                    <Download className="w-3.5 h-3.5" /> Export CSV
                  </button>
                </div>
              </div>


              {/* Main Line Charts section */}
              <div className="bg-[#FFFDF9] rounded-[18px] border border-[#E4D8C8] shadow-earth p-6">
                <div className="flex items-center justify-between mb-6 pb-3 border-b border-[#E4D8C8]">
                  <div className="flex items-center gap-2">
                    <BarChart2 className="w-4 h-4 text-[#1F5A43]" />
                    <h3 className="font-bold text-[#243238] uppercase text-xs tracking-wider">Growth Trends (Interactive Line Charts)</h3>
                  </div>
                  <span className="text-[10px] text-[#8D8A83] font-semibold uppercase">Realtime Trends</span>
                </div>
                
                <div className="h-[400px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data.recentGrowth} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#DDD0BE" opacity={0.3} />
                      <XAxis dataKey="month" stroke="#8D8A83" fontSize={11} tickLine={false} />
                      <YAxis yAxisId="left" stroke="#1F5A43" fontSize={11} tickLine={false} label={{ value: 'Revenue (₹)', angle: -90, position: 'insideLeft', style: { fill: '#1F5A43', fontSize: 11, fontWeight: 'bold' } }} />
                      <YAxis yAxisId="right" orientation="right" stroke="#8A4B22" fontSize={11} tickLine={false} label={{ value: 'Clients Count', angle: 90, position: 'insideRight', style: { fill: '#8A4B22', fontSize: 11, fontWeight: 'bold' } }} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#FFFDF9', borderRadius: '12px', border: '1px solid #E4D8C8', boxShadow: '0 8px 24px rgba(73,52,33,0.08)' }}
                        labelStyle={{ fontWeight: 'bold', color: '#243238' }}
                      />
                      <Legend verticalAlign="top" height={36}/>
                      {(activeMetric === 'both' || activeMetric === 'revenue') && (
                        <Line 
                          yAxisId="left" 
                          type="monotone" 
                          dataKey="revenue" 
                          name="Revenue (INR)" 
                          stroke="#1F5A43" 
                          strokeWidth={3} 
                          activeDot={{ r: 8 }} 
                          dot={{ strokeWidth: 2, r: 4 }}
                        />
                      )}
                      {(activeMetric === 'both' || activeMetric === 'clients') && (
                        <Line 
                          yAxisId="right" 
                          type="monotone" 
                          dataKey="clients" 
                          name="Active Clients" 
                          stroke="#8A4B22" 
                          strokeWidth={3} 
                          activeDot={{ r: 8 }}
                          dot={{ strokeWidth: 2, r: 4 }}
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Grid split for breakdown details */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Plan Split Breakdown */}
                <div className="rounded-[18px] border border-[#E4D8C8] shadow-earth p-6 bg-[#FFFDF9]">
                  <h3 className="text-xs font-bold text-[#243238] uppercase tracking-wider mb-6 flex items-center gap-2 pb-3 border-b border-[#E4D8C8]">
                    <span className="w-2.5 h-2.5 rounded-full bg-[#1F5A43]" />
                    <span>Plan Share Distribution</span>
                  </h3>
                  <div className="space-y-4">
                    {[
                      { label: 'Basic Option', count: data.planSplit.basic, color: 'bg-[#8D8A83]', pct: data.overview.totalClients > 0 ? Math.round((data.planSplit.basic / data.overview.totalClients) * 100) : 0 },
                      { label: 'Professional Option', count: data.planSplit.professional, color: 'bg-[#1F5A43]', pct: data.overview.totalClients > 0 ? Math.round((data.planSplit.professional / data.overview.totalClients) * 100) : 0 },
                      { label: 'Enterprise Option', count: data.planSplit.enterprise, color: 'bg-[#8A4B22]', pct: data.overview.totalClients > 0 ? Math.round((data.planSplit.enterprise / data.overview.totalClients) * 100) : 0 },
                    ].map((p) => (
                      <div key={p.label} className="group hover:bg-[#F7F2EA]/60 p-2 rounded-xl transition-all">
                        <div className="flex items-center justify-between text-sm mb-1.5">
                          <span className="font-semibold text-[#243238]">{p.label}</span>
                          <span className="text-[#6F706C] font-bold">{p.count} accounts ({p.pct}%)</span>
                        </div>
                        <div className="w-full h-3 bg-[#EFE5D6] rounded-full overflow-hidden">
                          <div className={`h-full ${p.color} rounded-full transition-all`} style={{ width: `${p.pct}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Status breakdown grid */}
                <div className="rounded-[18px] border border-[#E4D8C8] shadow-earth p-6 bg-[#FFFDF9]">
                  <h3 className="text-xs font-bold text-[#243238] uppercase tracking-wider mb-6 flex items-center gap-2 pb-3 border-b border-[#E4D8C8]">
                    <span className="w-2.5 h-2.5 rounded-full bg-[#8A4B22]" />
                    <span>Client Status Breakdown</span>
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { label: 'Active Subscription', count: data.overview.active, color: 'text-[#1F7A4F] bg-[#DFF5E7] border-[#1F7A4F]/20' },
                      { label: 'Trial Mode', count: data.overview.trial, color: 'text-[#C47C14] bg-[#FFF3D8] border-[#C47C14]/20' },
                      { label: 'Suspended Account', count: data.overview.suspended, color: 'text-[#D64545] bg-[#FCE8E8] border-[#D64545]/20' },
                      { label: 'Expired Contract', count: data.overview.expired, color: 'text-[#8A4B22] bg-[#EFE5D6] border-[#8A4B22]/20' },
                    ].map((s) => (
                      <div key={s.label} className={`rounded-xl p-4 border transition-all hover:scale-[1.01] ${s.color}`}>
                        <p className="text-2xl font-bold">{s.count}</p>
                        <p className="text-[10px] font-bold uppercase tracking-wider mt-1">{s.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Reports;
